import { useCallback, useEffect } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { MessageSquarePlus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useThreadStore } from '@/stores/thread-store'
import { deleteThreadEverywhere, pullThreads } from '@/features/companion/thread-sync'

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString()
}

interface ThreadListProps {
  onSelectThread: (id: string) => void
  onNewThread: () => void
  /** Called after any row action so the containing surface can close. */
  onDone?: () => void
}

/**
 * The thread list every history surface shares: a "New chat" row on top,
 * then the stored threads newest-first (title, origin page, relative time),
 * with hover-reveal delete. Reads the index from IDB on mount; the sync
 * layer's pull refreshes the same store.
 */
export function CompanionHistoryList({ onSelectThread, onNewThread, onDone }: ThreadListProps) {
  const threads = useThreadStore((s) => s.threads)
  const setThreads = useThreadStore((s) => s.setThreads)
  const removeThread = useThreadStore((s) => s.removeThread)
  const activeThreadId = useThreadStore((s) => s.activeThreadId)

  useEffect(() => {
    // Local index immediately, merged with the server list when reachable.
    pullThreads().then(setThreads)
  }, [setThreads])

  const handleSelect = useCallback(
    (id: string) => {
      onSelectThread(id)
      onDone?.()
    },
    [onSelectThread, onDone],
  )

  const handleNew = useCallback(() => {
    onNewThread()
    onDone?.()
  }, [onNewThread, onDone])

  const handleDelete = useCallback(
    async (e: React.MouseEvent | React.KeyboardEvent, id: string) => {
      e.stopPropagation()
      if (!window.confirm('Delete this conversation? This cannot be undone.')) return
      await deleteThreadEverywhere(id)
      removeThread(id)
      if (id === activeThreadId) onNewThread()
    },
    [removeThread, activeThreadId, onNewThread],
  )

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* New chat */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleNew}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleNew()
        }}
        className="flex w-full shrink-0 cursor-pointer items-center gap-2 border-b border-border px-3 py-2.5 text-sm text-muted-foreground hover:bg-hover hover:text-foreground"
      >
        <MessageSquarePlus size={14} />
        New chat
      </div>

      {/* Thread list */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground/60">
            No conversations yet
          </div>
        ) : (
          threads.map((entry) => (
            <div
              key={entry.id}
              role="button"
              tabIndex={0}
              onClick={() => handleSelect(entry.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSelect(entry.id)
              }}
              className={cn(
                'group flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-hover',
                entry.id === activeThreadId && 'bg-accent/50',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-foreground">
                  {entry.title || 'Untitled'}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <span>{entry.page}</span>
                  <span>{formatRelativeTime(entry.updatedAt)}</span>
                  <span>{entry.messageCount} msgs</span>
                </div>
              </div>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => handleDelete(e, entry.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation()
                    handleDelete(e, entry.id)
                  }
                }}
                className="hidden shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive group-hover:flex"
                title="Delete conversation"
                aria-label="Delete conversation"
              >
                <Trash2 size={12} />
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

interface CompanionHistoryPanelProps {
  onSelectThread: (id: string) => void
  onNewThread: () => void
}

/**
 * The drawer's history slide-over: opens from the hamburger INSIDE the
 * drawer's own surface (not a viewport sheet — the workspace stays visible),
 * with a scrim over the remaining strip. ESC or the scrim closes it.
 */
export function CompanionHistoryPanel({ onSelectThread, onNewThread }: CompanionHistoryPanelProps) {
  const historyOpen = useThreadStore((s) => s.historyOpen)
  const setHistoryOpen = useThreadStore((s) => s.setHistoryOpen)
  const reducedMotion = useReducedMotion()

  useEffect(() => {
    if (!historyOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setHistoryOpen(false)
      }
    }
    // Capture phase so the drawer's own ESC-to-close doesn't fire first.
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [historyOpen, setHistoryOpen])

  const close = useCallback(() => setHistoryOpen(false), [setHistoryOpen])

  return (
    <AnimatePresence>
      {historyOpen && (
        <>
          {/* Scrim over the drawer only */}
          <motion.div
            className="absolute inset-0 z-20 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={close}
            aria-hidden
          />
          <motion.div
            role="dialog"
            aria-label="Conversation history"
            className="absolute inset-y-0 left-0 z-30 w-[300px] border-r border-border bg-background shadow-xl"
            initial={reducedMotion ? { opacity: 0 } : { x: '-100%' }}
            animate={reducedMotion ? { opacity: 1 } : { x: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { x: '-100%' }}
            transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
          >
            <CompanionHistoryList
              onSelectThread={onSelectThread}
              onNewThread={onNewThread}
              onDone={close}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
